if (!customElements.get('rs3gt-color-teleport')) {
  customElements.define('rs3gt-color-teleport', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.host = this.closest('rs7gt-launch-floating');
      if (!this.host?.entries?.length) return;
      this.controller = new AbortController();
      this.animations = [];
      this.generation = 0;
      this.originalCommit = this.host.commit;
      this.wrappedCommit = (index, options = {}) => {
        const previous = this.host.currentStep;
        const animate = previous !== index && !options.instant && this.host.motionEnabled
          && this.host.isSceneVisible() && this.host.entries[previous]?.image.naturalWidth
          && typeof this.animate === 'function';
        this.cancel();
        this.originalCommit.call(this.host, index, options);
        this.updateNavigation();
        if (animate) this.play(previous, index);
      };
      this.host.commit = this.wrappedCommit;
      this.host.setAttribute('data-rs3-teleport-ready', '');
      const options = { signal: this.controller.signal };
      this.host.addEventListener('click', event => {
        const button = event.target.closest('[data-rs3-floating-step]');
        if (!button || !this.host.contains(button) || button.disabled) return;
        const next = Math.max(0, Math.min(this.host.entries.length - 1,
          this.host.desiredStep + Number(button.dataset.rs3FloatingStep)));
        this.host.select(next);
        this.updateNavigation();
      }, options);
      this.updateNavigation();
      this.host.motionQuery.addEventListener('change', () => this.cancel(), options);
      this.host.launchRoot?.addEventListener('rs7gt:motionchange', () => this.cancel(), options);
      this.host.launchRoot?.addEventListener('rs7gt:pagecoveragechange', () => {
        if (!this.host.isSceneVisible()) this.cancel();
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.cancel();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        if (this.host.contains(event.target)) this.cancel();
      }, options);
      window.addEventListener('resize', () => this.cancel(), { ...options, passive: true });
      this.observer = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) this.cancel();
      });
      this.observer.observe(this.host.stage);
    }

    get running() { return this.hasAttribute('data-running'); }

    updateNavigation() {
      this.host.querySelectorAll('[data-rs3-floating-step]').forEach(button => {
        const direction = Number(button.dataset.rs3FloatingStep);
        button.disabled = direction < 0 ? this.host.desiredStep <= 0 : this.host.desiredStep >= this.host.entries.length - 1;
      });
    }

    makeLayer(entry) {
      const crop = entry.slide.querySelector('.rs7-floating__crop');
      const bounds = crop.getBoundingClientRect();
      const image = entry.image.getBoundingClientRect();
      const fit = Math.min(image.width / entry.image.naturalWidth, image.height / entry.image.naturalHeight);
      const contentHeight = entry.image.naturalHeight * fit;
      const contentTop = image.top - bounds.top + (image.height - contentHeight) / 2;
      const stage = this.host.stage.getBoundingClientRect();
      const layer = document.createElement('div');
      layer.className = 'rs3-teleport__layer';
      Object.assign(layer.style, {
        left: `${bounds.left - stage.left}px`, top: `${bounds.top - stage.top}px`,
        width: `${bounds.width}px`, height: `${bounds.height}px`
      });
      this.append(layer);
      return { layer, crop, width: bounds.width, height: bounds.height, contentTop, contentHeight };
    }

    makeSlice(entry, geometry, band) {
      const { crop, height, contentTop, contentHeight } = geometry;
      const slice = document.createElement('div');
      slice.className = 'rs3-teleport__slice';
      const top = contentTop + band * contentHeight / 8;
      const bottom = height - contentTop - (band + 1) * contentHeight / 8;
      slice.style.clipPath = `inset(${Math.max(0, top)}px 0 ${Math.max(0, bottom)}px 0)`;
      const copy = crop.cloneNode(false);
      const image = entry.image.cloneNode(false);
      image.removeAttribute('id');
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      ['data-floating-image', 'data-floating-src', 'data-floating-srcset'].forEach(name => image.removeAttribute(name));
      image.src = entry.image.currentSrc || entry.image.src;
      image.alt = '';
      image.loading = 'eager';
      copy.append(image);
      slice.append(copy);
      return slice;
    }

    play(from, to) {
      const generation = this.generation;
      this.host.setAttribute('data-rs3-teleport-active', '');
      this.setAttribute('data-running', '');
      const direction = Math.sign(to - from) || 1;
      [from, to].forEach((index, phase) => {
        const entry = this.host.entries[index];
        const geometry = this.makeLayer(entry);
        const { layer, width } = geometry;
        const distance = Math.min(64, width * 0.065);
        for (let band = 0; band < 8; band++) {
          const slice = this.makeSlice(entry, geometry, band);
          layer.append(slice);
          const shift = distance * (band % 2 ? -1 : 1) * direction;
          const dispersed = `translate3d(${shift}px, ${(band - 3.5) * 1.2}px, 0) scaleX(1.045)`;
          const frames = phase === 0
            ? [{ transform: 'none', opacity: 1 },
              { transform: `translateX(${shift * 0.15}px)`, opacity: .85, offset: .3 },
              { transform: dispersed, opacity: 0 }]
            : [{ transform: dispersed, opacity: 0 },
              { transform: `translateX(${-shift * 0.06}px)`, opacity: .9, offset: .72 },
              { transform: 'none', opacity: 1 }];
          this.animations.push(slice.animate(frames, {
            duration: phase === 0 ? 210 : 280,
            delay: phase === 0 ? band * 10 : 310 + (7 - band) * 8,
            easing: phase === 0 ? 'cubic-bezier(.55, 0, 1, .45)' : 'cubic-bezier(.16, 1, .3, 1)',
            fill: 'both'
          }));
        }
      });
      Promise.all(this.animations.map(animation => animation.finished)).then(() => {
        if (generation === this.generation) this.cancel();
      }).catch(() => { /* Replacing a color or leaving the scene cancels the effect. */ });
    }

    cancel() {
      this.generation++;
      this.animations?.forEach(animation => animation.cancel());
      this.animations = [];
      this.replaceChildren();
      this.removeAttribute('data-running');
      this.host?.removeAttribute('data-rs3-teleport-active');
    }

    disconnectedCallback() {
      this.cancel();
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      if (this.host?.commit === this.wrappedCommit) this.host.commit = this.originalCommit;
      this.host?.removeAttribute('data-rs3-teleport-ready');
    }
  });
}
